import {
	FormItem,
	InputWithMoreInfo,
	ReorderableList,
	Select,
	SelectItem,
	TextInput,
	ValidationError
} from "client/Components";
import { SharePointFeedInformationDialog } from "client/Features/Workspace/LeftSliders/EditTile/Dialogs/SharePointFeedInformationDialog";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	getClientId,
	hasClientId,
	setClientId
} from "client/Misc/clientId";
import { createValidationConfiguration } from "client/Validation";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import {
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { RssFeedType } from "client/Api/Workspace/LiveInformation/Rss/rssLiveInformation";
import { AppType } from "client/Api/Apps/common";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	EditRssAppViewModel,
	SaveRssAppModel,
	PredefinedRssFeedModel,
	saveRssApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditRssAppProps = {
	initialSettings: EditAppSettings<EditRssAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditRssApp = forwardRef(
	(
		props: EditRssAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [isSharePointFeedInformationDialogOpen, setIsSharePointFeedInformationDialogOpen] = useState<boolean>(false);
		const [formData, setFormData] = useState<SaveRssAppModel>(
			() => {
				const {
					feeds
				} = initialSettings
					.model
					.app;

				return {
					...initialGenericState(initialSettings),
					feeds
				};
			}
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveRssAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveRssAppModel>(
			formHandler,
			saveRssApp,
			AppType.Rss
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const removeFeedHandler = (index: number): void => {
			formHandler.for("feeds")
				.reducer
				.removeAt(index);
		};

		const addFeedHandler = (): void => {
			const feedsHandler: IValueInputAttachable<PredefinedRssFeedModel[]> = formHandler
				.for("feeds");

			feedsHandler.reducer
				.push(
					setClientId({
						id: null,
						name: "",
						feedUrl: "",
						feedType: DEFAULT_FEED_TYPE,
						siteUrl: ""
					})
				);
			feedsHandler.validation
				.triggerInputFinished();
		};

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("feeds")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<h2>{localize("_PredefinedRSSFeeds")}</h2>
				<FormItem
					hasNoMargin
				>
					<ReorderableList>
						{formHandler.for("feeds")
							.map((feedHandler, index) => {
									const {
										feedType
									} = feedHandler.value;
									const feedUrlHandler: IValueInputAttachable<string> = feedHandler
										.for("feedUrl");

									return (
										<ReorderableList.PanelItem
											{...feedHandler.attachToReorderableListPanelItem(true)}
											key={index}
											header={
												<TextInput
													{...feedHandler.for("name").attachToTextInput()}
													placeholder={localize("_Name")}
												/>
											}
											onRemove={() => removeFeedHandler(index)}
										>
											<ValidationError {...feedHandler.for("name").attachToValidationError()} />
											<FormItem
												label={localize("_FeedType")}
											>
												<Select
													{...feedHandler.for("feedType").attachToSelect()}
													options={feedTypeOptions}
													menuTitle={localize("_FeedType")}
												/>
											</FormItem>
											<FormItem
												label={localize("_RssFeedUrl")}
											>
												{shouldShowInformationIcon(feedType)
													? (
														<InputWithMoreInfo
															onMoreInfo={() => setIsSharePointFeedInformationDialogOpen(true)}
														>
															<TextInput {...feedUrlHandler.attachToTextInput()} />
														</InputWithMoreInfo>
													)
													: (
														<TextInput {...feedUrlHandler.attachToTextInput()} />
													)
												}
												<ValidationError {...feedUrlHandler.attachToValidationError()} />
											</FormItem>
											<FormItem
												label={localize("_SiteUrl")}
											>
												<TextInput {...feedHandler.for("siteUrl").attachToTextInput()} />
												<ValidationError {...feedHandler.for("siteUrl").attachToValidationError()} />
											</FormItem>
										</ReorderableList.PanelItem>
									);
								}
							)}
					</ReorderableList>
					<ReorderableList.AddButton
						trackingId="add-feed-button"
						onClick={addFeedHandler}
					/>
					{isSharePointFeedInformationDialogOpen && (
						<SharePointFeedInformationDialog
							onClose={() => setIsSharePointFeedInformationDialogOpen(false)}
						/>
					)}
				</FormItem>
			</EditAppBase>
		);
	}
);
EditRssApp.displayName = "EditRssApp";

const feedTypeOptions: readonly SelectItem<RssFeedType>[] = [
	{
		text: localize("_PublicRSS"),
		value: RssFeedType.Public
	},
	{
		text: localize("_SharePoint"),
		value: RssFeedType.SharePoint
	}
];

export const DEFAULT_FEED_TYPE: RssFeedType = RssFeedType.Public;

const validation = createValidationConfiguration<SaveRssAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

const feedsValidation: IObjectValidatorBuilder<PredefinedRssFeedModel> = validation
	.forCollection("feeds")
	.forItems(getValidationIdForFeed);

feedsValidation.forScalar("feedUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: false
		})
	);

feedsValidation.forScalar("siteUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: false
		})
	);

feedsValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

function shouldShowInformationIcon(feedType: RssFeedType): boolean {
	return feedType === RssFeedType.SharePoint;
}

function getValidationIdForFeed(feed: PredefinedRssFeedModel): string {
	if (feed.id) {
		return feed.id.toString();
	}

	if (hasClientId(feed)) {
		return getClientId(feed);
	}

	throw Error("Client id is not set on the FeedType object");
}


